import * as React from "react"
import { useNavigate } from "react-router-dom"
import { useQuery, useQueryClient } from "react-query"
import { Box, Button, Flex, Text, useTheme } from "@chakra-ui/react"
import { FaRegHandPaper } from "react-icons/fa"

import { Loading } from "../Loading"
import { useMe } from "./MeProvider"
import { AUTH_TOKEN, REFRESH_TOKEN } from "../../lib/config"
import { DEFAULT_ORG_KEY } from "../../lib/config"
import { OrgEntity, OrgsResponse } from "../../lib/types/org"
import { useToast } from "../../lib/hooks/useToast"
import { ProviderProps } from "./types"

export const OrgProvider: React.FC<ProviderProps> = ({ children }) => {
  const me = useMe()
  const navigate = useNavigate()
  const client = useQueryClient()
  const toast = useToast()
  const theme = useTheme()

  const [currentOrgs, setOrgs] = React.useState<OrgEntity[] | null>(null)
  const [noOrg, setNoOrg] = React.useState(false)
  const { isLoading, data } = useQuery<OrgsResponse, string>(
    `organizations/?user=${me.uuid}&page_size=999&page=1`,
    {
      staleTime: Infinity,
      enabled: !!!currentOrgs,
    },
  )

  const handleSetOrgs = React.useCallback((org: OrgEntity[]) => {
    localStorage.setItem(DEFAULT_ORG_KEY, JSON.stringify(org))
    setOrgs(org)
  }, [])

  const handleLogout = () => {
    navigate("/")
    localStorage.removeItem(AUTH_TOKEN)
    localStorage.removeItem(REFRESH_TOKEN)
    client.setQueryData("me/", () => undefined)
    toast({ description: "Successfully logged out!" })
  }

  const getStoredOrgs = React.useCallback(async () => {
    try {
      if (isLoading) return
      const orgs = data?.results
      if (orgs && orgs.length > 0) {
        const storedOrgs: OrgEntity[] | undefined =
          localStorage.getItem(DEFAULT_ORG_KEY) && JSON.parse(localStorage.getItem(DEFAULT_ORG_KEY) || "")
        if (storedOrgs && storedOrgs.length > 0) {
          const filteredOrgs = storedOrgs.filter((stored) => orgs.find((e) => e.uuid === stored.uuid))
          if (filteredOrgs && filteredOrgs.length > 0) {
            handleSetOrgs(filteredOrgs)
            setNoOrg(false)
            return
          }
        }
        const firstOrg = orgs.sort((a: OrgEntity, b: OrgEntity) => (a.name > b.name ? 1 : -1))[0]
        handleSetOrgs([firstOrg])
        setNoOrg(false)
      } else {
        setNoOrg(true)
      }
    } catch (e) {
      setNoOrg(true)
      console.error("Error fetching orgs:", e)
    }
  }, [data, handleSetOrgs, isLoading])

  React.useEffect(() => {
    if (!currentOrgs) {
      getStoredOrgs()
    }
  }, [currentOrgs, getStoredOrgs])

  if (noOrg) {
    return (
      <Flex align="center" justify="center" h="100vh" w="100vw" direction="column">
        <Box as={FaRegHandPaper} boxSize="120px" color={theme.colors.gray[600]} mb={6} />
        <Text fontSize="1.8rem" fontWeight="bold" textAlign="center" mb={12} color={theme.colors.gray[600]}>
          Insufficient Permissions
        </Text>
        <Text fontSize="1.2rem" textAlign="center" mb={12} color={theme.colors.gray[500]}>
          You must have at least one affiliation with an organization.
        </Text>
        <Button size="md" variant="outline" onClick={handleLogout}>
          Logout
        </Button>
      </Flex>
    )
  }
  if (!currentOrgs) return <Loading />
  return <OrgContext.Provider value={{ currentOrgs, setOrgs: handleSetOrgs }}>{children}</OrgContext.Provider>
}

export const OrgContext = React.createContext<
  { currentOrgs: OrgEntity[]; setOrgs: (org: OrgEntity[]) => void } | undefined
>(undefined)

export function useOrgs() {
  const context = React.useContext(OrgContext)
  if (!context) throw new Error("Must be called under OrgProvider")
  return context
}
